import {
	Card,
	Form,
	Input,
	Button,
	Select,
	Typography,
	Row,
	Col,
	Space,
	notification,
	Divider,
	InputNumber,
	Switch,
	Checkbox,
} from "antd";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { PiGlobeDuotone, PiArrowLeftDuotone, PiFloppyDiskDuotone } from "react-icons/pi";
import { requestCreateDomainExtension } from "../../services/admin.service";

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const CATEGORIES = [
	{ value: "GENERIC", label: "Generic (.com, .net, .org)" },
	{ value: "COUNTRY", label: "Country (.id, .us, .uk)" },
	{ value: "SPONSORED", label: "Sponsored (.edu, .gov, .mil)" },
	{ value: "NEW_GENERIC", label: "New Generic (.app, .blog, .shop)" },
	{ value: "REGIONAL", label: "Regional (.asia, .eu)" },
	{ value: "OTHER", label: "Other" },
];

const STATUSES = [
	{ value: "ACTIVE", label: "Active - Available for purchase" },
	{ value: "INACTIVE", label: "Inactive - Not available" },
	{ value: "COMING_SOON", label: "Coming Soon - Will be available soon" },
	{ value: "DEPRECATED", label: "Deprecated - Being phased out" },
];

const REGISTRANT_TYPES = [
	{ value: "INDIVIDUAL", label: "Individual" },
	{ value: "ORGANIZATION", label: "Organization" },
	{ value: "BOTH", label: "Both Individual & Organization" },
];

const REGISTRARS = [
	{ value: "RDASH", label: "RDASH" },
	{ value: "SPACESHIP", label: "SPACESHIP" },
];

function AddDomainExtensionPage() {
	const [form] = Form.useForm();
	const navigate = useNavigate();
	const [loading, setLoading] = useState(false);

	const handleSubmit = async (values) => {
		setLoading(true);
		try {
			// Transform form data to match schema
			const payload = {
				code: values.code,
				name: values.name,
				description: values.description,
				category: values.category,
				status: values.status,
				price: {
					registration: {
						amount: values.registrationPrice || 0,
					},
					renewal: {
						amount: values.renewalPrice || 0,
					},
					transfer: {
						amount: values.transferPrice || 0,
					},
					redemption: {
						amount: values.redemptionPrice || 0,
					},
				},
				features: {
					autoRenewal: values.autoRenewal ?? true,
					transferLock: values.transferLock ?? true,
					dnsManagement: values.dnsManagement ?? true,
				},
				restrictions: {
					minRegistrationPeriod: values.minRegistrationPeriod || 1,
					maxRegistrationPeriod: values.maxRegistrationPeriod || 10,
					minRenewalPeriod: values.minRenewalPeriod || 1,
					maxRenewalPeriod: values.maxRenewalPeriod || 10,
					requiresDocumentation: values.requiresDocumentation ?? false,
					restrictedCountries: values.restrictedCountries
						? values.restrictedCountries.split(",").map((c) => c.trim())
						: [],
					allowedRegistrantTypes: values.allowedRegistrantTypes || ["BOTH"],
				},
				registrar: {
					code: values.registrarCode,
					name: values.registrarCode, // Using the same value for both code and name as per requirement
					wholesale: values.registrarWholesale ?? false,
					apiSupported: values.registrarApiSupported ?? false,
					manualRegistration: values.registrarManualRegistration ?? true,
				},
				popularity: {
					rank: values.popularityRank || 999,
					orderCount: 0,
					isPopular: values.isPopular ?? false,
					isFeatured: values.isFeatured ?? false,
				},
				seo: {
					title: values.seoTitle,
					description: values.seoDescription,
					keywords: values.seoKeywords ? values.seoKeywords.split(",").map((k) => k.trim()) : [],
				},
				metadata: values.metadata ? JSON.parse(values.metadata) : {},
			};

			await requestCreateDomainExtension(payload);

			notification.success({
				message: "Success",
				description: "Domain extension created successfully",
			});

			navigate("/domain-extensions");
		} catch (error) {
			console.error("Error creating domain extension:", error);
			notification.error({
				message: "Error",
				description: error.response?.data?.message || "Failed to create domain extension",
			});
		} finally {
			setLoading(false);
		}
	};

	return (
		<div className="page-content">
			<div style={{ marginBottom: 24 }}>
				<Space>
					<Button
						icon={<PiArrowLeftDuotone />}
						onClick={() => navigate("/domain-extensions")}></Button>
					<Title level={4} style={{ margin: 0 }}>
						Add Domain Extension
					</Title>
				</Space>
			</div>

			<div className="page-content-wrapper">
				<Form
					form={form}
					layout="vertical"
					onFinish={handleSubmit}
					initialValues={{
						category: "GENERIC",
						status: "ACTIVE",
						autoRenewal: true,
						transferLock: true,
						dnsManagement: true,
						requiresDocumentation: false,
						minRegistrationPeriod: 1,
						maxRegistrationPeriod: 10,
						minRenewalPeriod: 1,
						maxRenewalPeriod: 10,
						registrarWholesale: false,
						registrarApiSupported: false,
						registrarManualRegistration: true,
						popularityRank: 999,
						isPopular: false,
						isFeatured: false,
						allowedRegistrantTypes: ["BOTH"],
						registrarCode: "RDASH", // Default registrar
					}}>
					<Row gutter={24}>
						<Col xs={24} lg={16}>
							<Card title="Basic Information" style={{ marginBottom: 24 }}>
								<Row gutter={16}>
									<Col xs={24} md={12}>
										<Form.Item
											label="Extension Code"
											name="code"
											rules={[
												{ required: true, message: "Please enter extension code" },
												{ pattern: /^[A-Z.]+$/, message: "Use uppercase letters and dots only" },
											]}
											tooltip="Examples: COM, NET, ORG, CO.ID">
											<Input placeholder="e.g., COM, NET, CO.ID" />
										</Form.Item>
									</Col>
									<Col xs={24} md={12}>
										<Form.Item
											label="Display Name"
											name="name"
											rules={[{ required: true, message: "Please enter display name" }]}
											tooltip="Examples: .com, .net, .co.id">
											<Input placeholder="e.g., .com, .net, .co.id" />
										</Form.Item>
									</Col>
								</Row>
								<Form.Item
									label="Description"
									name="description"
									tooltip="Brief description of the domain extension">
									<TextArea rows={3} placeholder="e.g., Commercial domains, Indonesia domains" />
								</Form.Item>
								<Row gutter={16}>
									<Col xs={24} md={12}>
										<Form.Item
											label="Category"
											name="category"
											rules={[{ required: true, message: "Please select category" }]}>
											<Select placeholder="Select category">
												{CATEGORIES.map((cat) => (
													<Option key={cat.value} value={cat.value}>
														{cat.label}
													</Option>
												))}
											</Select>
										</Form.Item>
									</Col>
									<Col xs={24} md={12}>
										<Form.Item
											label="Status"
											name="status"
											rules={[{ required: true, message: "Please select status" }]}>
											<Select placeholder="Select status">
												{STATUSES.map((status) => (
													<Option key={status.value} value={status.value}>
														{status.label}
													</Option>
												))}
											</Select>
										</Form.Item>
									</Col>
								</Row>
							</Card>

							<Card title="Pricing" style={{ marginBottom: 24 }}>
								<Row gutter={16}>
									<Col xs={24} md={12}>
										<Form.Item
											label="Registration Price ($)"
											name="registrationPrice"
											rules={[{ required: true, message: "Please enter registration price" }]}>
											<InputNumber
												min={0}
												step={0.01}
												style={{ width: "100%" }}
												placeholder="0.00"
											/>
										</Form.Item>
									</Col>
									<Col xs={24} md={12}>
										<Form.Item
											label="Renewal Price ($)"
											name="renewalPrice"
											rules={[{ required: true, message: "Please enter renewal price" }]}>
											<InputNumber
												min={0}
												step={0.01}
												style={{ width: "100%" }}
												placeholder="0.00"
											/>
										</Form.Item>
									</Col>
								</Row>
								<Row gutter={16}>
									<Col xs={24} md={12}>
										<Form.Item
											label="Transfer Price ($)"
											name="transferPrice"
											rules={[{ required: true, message: "Please enter transfer price" }]}>
											<InputNumber
												min={0}
												step={0.01}
												style={{ width: "100%" }}
												placeholder="0.00"
											/>
										</Form.Item>
									</Col>
									<Col xs={24} md={12}>
										<Form.Item label="Redemption Price ($)" name="redemptionPrice">
											<InputNumber
												min={0}
												step={0.01}
												style={{ width: "100%" }}
												placeholder="0.00"
											/>
										</Form.Item>
									</Col>
								</Row>
							</Card>

							<Card title="Features" style={{ marginBottom: 24 }}>
								<Row gutter={16}>
									<Col xs={24} md={8}>
										<Form.Item name="autoRenewal" valuePropName="checked">
											<Switch />
											<span style={{ marginLeft: 8 }}>Auto Renewal</span>
										</Form.Item>
									</Col>
									<Col xs={24} md={8}>
										<Form.Item name="transferLock" valuePropName="checked">
											<Switch />
											<span style={{ marginLeft: 8 }}>Transfer Lock</span>
										</Form.Item>
									</Col>
									<Col xs={24} md={8}>
										<Form.Item name="dnsManagement" valuePropName="checked">
											<Switch />
											<span style={{ marginLeft: 8 }}>DNS Management</span>
										</Form.Item>
									</Col>
								</Row>
							</Card>

							<Card title="Restrictions" style={{ marginBottom: 24 }}>
								<Row gutter={16}>
									<Col xs={24} md={12}>
										<Form.Item label="Min Registration Period (years)" name="minRegistrationPeriod">
											<InputNumber min={1} max={10} style={{ width: "100%" }} />
										</Form.Item>
									</Col>
									<Col xs={24} md={12}>
										<Form.Item label="Max Registration Period (years)" name="maxRegistrationPeriod">
											<InputNumber min={1} max={10} style={{ width: "100%" }} />
										</Form.Item>
									</Col>
								</Row>
								<Row gutter={16}>
									<Col xs={24} md={12}>
										<Form.Item label="Min Renewal Period (years)" name="minRenewalPeriod">
											<InputNumber min={1} max={10} style={{ width: "100%" }} />
										</Form.Item>
									</Col>
									<Col xs={24} md={12}>
										<Form.Item label="Max Renewal Period (years)" name="maxRenewalPeriod">
											<InputNumber min={1} max={10} style={{ width: "100%" }} />
										</Form.Item>
									</Col>
								</Row>
								<Form.Item name="requiresDocumentation" valuePropName="checked">
									<Checkbox>Requires Documentation</Checkbox>
								</Form.Item>
								<Form.Item
									label="Restricted Countries"
									name="restrictedCountries"
									tooltip="Comma-separated country codes (e.g., US, UK, ID)">
									<Input placeholder="US, UK, ID" />
								</Form.Item>
								<Form.Item label="Allowed Registrant Types" name="allowedRegistrantTypes">
									<Checkbox.Group options={REGISTRANT_TYPES} />
								</Form.Item>
							</Card>
						</Col>

						<Col xs={24} lg={8}>
							<Card title="Registrar Information" style={{ marginBottom: 24 }}>
								<Form.Item
									label="Registrar"
									name="registrarCode"
									rules={[{ required: true, message: "Please select a registrar" }]}>
									<Select placeholder="Select registrar">
										{REGISTRARS.map((registrar) => (
											<Option key={registrar.value} value={registrar.value}>
												{registrar.label}
											</Option>
										))}
									</Select>
								</Form.Item>
								<Form.Item name="registrarWholesale" valuePropName="checked">
									<Switch />
									<span style={{ marginLeft: 8 }}>Wholesale</span>
								</Form.Item>
								<Form.Item name="registrarApiSupported" valuePropName="checked">
									<Switch />
									<span style={{ marginLeft: 8 }}>API Supported</span>
								</Form.Item>
								<Form.Item name="registrarManualRegistration" valuePropName="checked">
									<Switch />
									<span style={{ marginLeft: 8 }}>Manual Registration</span>
								</Form.Item>
							</Card>

							<Card title="Popularity & Marketing" style={{ marginBottom: 24 }}>
								<Form.Item
									label="Popularity Rank"
									name="popularityRank"
									tooltip="Lower number = more popular">
									<InputNumber min={1} style={{ width: "100%" }} />
								</Form.Item>
								<Form.Item name="isPopular" valuePropName="checked">
									<Switch />
									<span style={{ marginLeft: 8 }}>Popular</span>
								</Form.Item>
								<Form.Item name="isFeatured" valuePropName="checked">
									<Switch />
									<span style={{ marginLeft: 8 }}>Featured</span>
								</Form.Item>
							</Card>

							<Card title="SEO" style={{ marginBottom: 24 }}>
								<Form.Item label="SEO Title" name="seoTitle">
									<Input placeholder="SEO page title" />
								</Form.Item>
								<Form.Item label="SEO Description" name="seoDescription">
									<TextArea rows={3} placeholder="SEO meta description" />
								</Form.Item>
								<Form.Item
									label="SEO Keywords"
									name="seoKeywords"
									tooltip="Comma-separated keywords">
									<Input placeholder="domain, registration, extension" />
								</Form.Item>
							</Card>

							<Card title="Metadata (JSON)" style={{ marginBottom: 24 }}>
								<Form.Item label="Additional Metadata" name="metadata" tooltip="Valid JSON object">
									<TextArea rows={4} placeholder='{"key": "value"}' />
								</Form.Item>
							</Card>
						</Col>
					</Row>

					<Card>
						<Space>
							<Button
								type="primary"
								htmlType="submit"
								loading={loading}
								icon={<PiFloppyDiskDuotone />}>
								Create Domain Extension
							</Button>
							<Button onClick={() => navigate("/domain-extensions")}>Cancel</Button>
						</Space>
					</Card>
				</Form>
			</div>
		</div>
	);
}

export default AddDomainExtensionPage;
